/**
 * Purchase order schemas (Compres i Recepció) — comanda de compra capçalera +
 * línies, plus the derived per-line fulfillment read shape.
 *
 * Convention (schemas/index.ts): `Entity` = read shape (tenant_id, timestamps,
 * soft-delete); `*Input` = client write payload, `.strict()`, NEVER accepts
 * tenant_id, omits server-generated values (order_number). The comanda's
 * fulfillment / "quantitat pendent d'entrega" is DERIVED (decision Q3), not a
 * stored per-line accumulator — see PurchaseOrderLineFulfillment (the
 * purchase_order_line_fulfillment view) and domain/purchasing.ts.
 */

import { z } from 'zod';
import {
  APP_LANGUAGES,
  PURCHASE_ORDER_CHANNELS,
  PURCHASE_ORDER_DISPATCH_RESULTS,
  PURCHASE_ORDER_STATUSES,
  RECEPTION_EXCEPTION_KINDS,
  RECEPTION_EXCEPTION_STATUSES,
  RECEPTION_EXCEPTION_REJECTIONS,
  PURCHASE_RETURN_STATUSES,
  PURCHASE_RETURN_ORIGINS,
  INCIDENT_RESOLUTIONS,
} from '../enums/index.js';
import { deliveryBeforeOrder } from '../domain/supplierDelivery.js';
import {
  uuid,
  nonEmptyString,
  dateString,
  nonNegativeQuantity,
  money,
  timestampFields,
  softDeleteFields,
} from './common.js';

/** A strictly positive ordered quantity (DB CHECK ordered_quantity > 0). */
const positiveQuantity = z.number().finite().positive();

// ── PurchaseOrderLine ────────────────────────────────────────────────────────

/**
 * One line of a comanda de compra. `purchase_unit_id` references the packaging
 * units master (Sac/Caixa/…); null = the article base unit. The radial factor to
 * base lives in article_packaging_units. No stored received accumulator: the
 * received / pending quantities are derived (see PurchaseOrderLineFulfillment).
 */
export const PurchaseOrderLine = z
  .object({
    id: uuid,
    tenant_id: uuid,
    purchase_order_id: uuid,
    article_id: uuid,
    ordered_quantity: nonNegativeQuantity,
    purchase_unit_id: uuid.nullable(),
    agreed_unit_price: money.nullable(),
    sort_order: z.number().int(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type PurchaseOrderLine = z.infer<typeof PurchaseOrderLine>;

/** Create/edit payload for a single comanda line. */
export const PurchaseOrderLineInput = z
  .object({
    article_id: uuid,
    ordered_quantity: positiveQuantity,
    purchase_unit_id: uuid.nullable().optional(),
    agreed_unit_price: money.nullable().optional(),
    sort_order: z.number().int().min(0).default(0),
    /**
     * Nota lliure d'AQUESTA línia, independent de la de la capçalera: la de dalt
     * parla de la comanda sencera, aquesta d'un article concret («d'aquest, el
     * lot més fresc que tinguis»). Opcional i sense cap regla al darrere.
     */
    notes: nonEmptyString.nullable().optional(),
  })
  .strict();
export type PurchaseOrderLineInput = z.infer<typeof PurchaseOrderLineInput>;

// ── PurchaseOrder ────────────────────────────────────────────────────────────

/**
 * L'entrega prevista no pot caure abans de la data de comanda (OBR-69). Només es
 * pot jutjar quan el payload porta totes dues dates; un pedaç que en porti una
 * sola el jutja el guard de la base de dades (purchase_orders_dates_guard), que
 * rebutja amb el mateix codi.
 */
export const PURCHASE_ORDER_DELIVERY_BEFORE_ORDER = 'purchase_order_delivery_before_order';

const deliveryNotBeforeOrder = (value: {
  readonly order_date?: string | undefined;
  readonly expected_delivery_date?: string | null | undefined;
}): boolean =>
  value.order_date === undefined ||
  !deliveryBeforeOrder(value.order_date, value.expected_delivery_date);

/**
 * Comanda de compra (capçalera). `order_number` is server-assigned (trigger,
 * never manual), so the read shape includes it but the input schemas do not.
 */
export const PurchaseOrder = z
  .object({
    id: uuid,
    tenant_id: uuid,
    order_number: nonEmptyString,
    supplier_id: uuid,
    order_date: dateString,
    expected_delivery_date: dateString.nullable(),
    status: z.enum(PURCHASE_ORDER_STATUSES),
    notes: z.string().trim().min(1).nullable(),
    created_by: uuid.nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type PurchaseOrder = z.infer<typeof PurchaseOrder>;

/**
 * Create payload for a comanda de compra (capçalera + línies). tenant_id and
 * order_number are NEVER accepted from the client. At least one line is required.
 */
export const PurchaseOrderCreateInput = z
  .object({
    supplier_id: uuid,
    order_date: dateString.optional(),
    expected_delivery_date: dateString.nullable().optional(),
    notes: nonEmptyString.nullable().optional(),
    lines: z.array(PurchaseOrderLineInput).min(1),
  })
  .strict()
  .refine(deliveryNotBeforeOrder, {
    message: PURCHASE_ORDER_DELIVERY_BEFORE_ORDER,
    path: ['expected_delivery_date'],
  });
export type PurchaseOrderCreateInput = z.infer<typeof PurchaseOrderCreateInput>;

/**
 * Header update payload (edit an existing comanda / advance its status). Lines
 * are edited through their own PurchaseOrderLineInput writes. Every field is
 * optional so a partial patch persists only what changed; `.strict()` rejects a
 * forged tenant_id / order_number.
 */
export const PurchaseOrderUpdateInput = z
  .object({
    supplier_id: uuid.optional(),
    order_date: dateString.optional(),
    expected_delivery_date: dateString.nullable().optional(),
    status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
    notes: nonEmptyString.nullable().optional(),
  })
  .strict()
  .refine(deliveryNotBeforeOrder, {
    message: PURCHASE_ORDER_DELIVERY_BEFORE_ORDER,
    path: ['expected_delivery_date'],
  });
export type PurchaseOrderUpdateInput = z.infer<typeof PurchaseOrderUpdateInput>;

// ── Enviament al proveïdor (OBR-79) ─────────────────────────────────────────────

/**
 * Un enviament (o una marca manual) d'una comanda al proveïdor —
 * purchase_order_dispatches. Append-only i escrit només per la funció
 * `purchase-order-send`: aquest és només el que se'n llegeix.
 */
export const PurchaseOrderDispatch = z
  .object({
    id: uuid,
    tenant_id: uuid,
    purchase_order_id: uuid,
    channel: z.enum(PURCHASE_ORDER_CHANNELS),
    recipients: z.array(z.string()),
    subject: z.string().nullable(),
    // El text que s'ha obert a WhatsApp (OBR-175); null en un correu, una marca manual o un enviament anterior.
    message: z.string().nullable(),
    language: z.enum(APP_LANGUAGES).nullable(),
    with_pdf: z.boolean(),
    result: z.enum(PURCHASE_ORDER_DISPATCH_RESULTS),
    error: z.string().nullable(),
    sent_by: uuid.nullable(),
    created_at: z.string(),
  })
  .strict();
export type PurchaseOrderDispatch = z.infer<typeof PurchaseOrderDispatch>;

/**
 * Què es demana a `purchase-order-send`. La funció en porta el mirall (Deno no
 * pot importar aquest paquet); si canvia aquí, ha de canviar allà.
 *
 *  · `orders`: les comandes, cadascuna amb el seu PDF (base64) quan n'ha de
 *    portar — el PDF es fa al navegador, com tots els d'Obrava.
 *  · `mode`: `default` = pel canal de cada proveïdor (correu, o constància si és
 *    manual); `manual` = només deixar constància que s'ha enviat per altres mitjans.
 *  · `via`: només amb `manual` i UNA comanda. `whatsapp` = deixar constància que la persona ha enviat la
 *    comanda pel WhatsApp del proveïdor (Obrava obre WhatsApp, però no pot saber si el missatge ha sortit;
 *    la constància és el que la persona declara). El número el posa el servidor, de la fitxa del proveïdor.
 *  · `message`: només amb `via: 'whatsapp'`. El text que s'ha obert a WhatsApp, perquè en quedi constància al
 *    registre (OBR-175). El composa el navegador (el servidor no el pot verificar: és el que la persona ha vist).
 *  · `recipients`: canviar excepcionalment a qui s'envia. Només amb UNA comanda.
 *  · `resend`: tornar a enviar una comanda que ja consta enviada.
 *  · `language`: l'idioma del PDF (vegeu el camp).
 */
export const PURCHASE_ORDER_SEND_MODES = ['default', 'manual'] as const;
/** Fins on arriba el text de WhatsApp que es desa al registre (la funció en porta el mirall). */
export const PURCHASE_ORDER_MESSAGE_MAX_LENGTH = 4000;
export const PurchaseOrderSendRequest = z
  .object({
    orders: z
      .array(
        z
          .object({
            id: uuid,
            pdf_base64: z.string().min(1).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(50),
    mode: z.enum(PURCHASE_ORDER_SEND_MODES),
    via: z.enum(['whatsapp']).optional(),
    message: z.string().min(1).max(PURCHASE_ORDER_MESSAGE_MAX_LENGTH).optional(),
    recipients: z.array(z.string().trim().email()).min(1).max(10).optional(),
    resend: z.boolean(),
    /**
     * L'idioma en què s'ha fet el PDF, perquè el correu digui el mateix. Només
     * mana quan el proveïdor no en té de propi; sense cap dels dos, el de l'obrador.
     */
    language: z.enum(APP_LANGUAGES).optional(),
  })
  .strict()
  .refine((value) => value.recipients === undefined || value.orders.length === 1, {
    message: 'recipients_need_single_order',
    path: ['recipients'],
  })
  .refine((value) => value.via === undefined || (value.mode === 'manual' && value.orders.length === 1), {
    message: 'via_needs_manual_single_order',
    path: ['via'],
  })
  .refine((value) => value.message === undefined || value.via === 'whatsapp', {
    message: 'message_needs_whatsapp',
    path: ['message'],
  });
export type PurchaseOrderSendRequest = z.infer<typeof PurchaseOrderSendRequest>;

/**
 * Què ha passat amb cada comanda: `sent` (ha sortit per correu), `marked` (en
 * consta l'enviament manual), `failed` (el correu no ha sortit; `reason` ho diu) o
 * `skipped` (no li tocava: `reason` és el motiu de purchaseOrderSendOutcome o
 * `no_recipients`).
 */
export const PURCHASE_ORDER_SEND_OUTCOMES = ['sent', 'marked', 'failed', 'skipped'] as const;
export type PurchaseOrderSendOutcomeKind = (typeof PURCHASE_ORDER_SEND_OUTCOMES)[number];
export const PurchaseOrderSendResult = z.object({
  id: uuid,
  order_number: z.string(),
  outcome: z.enum(PURCHASE_ORDER_SEND_OUTCOMES),
  reason: z.string().optional(),
  recipients: z.array(z.string()).optional(),
});
export type PurchaseOrderSendResult = z.infer<typeof PurchaseOrderSendResult>;
export const PurchaseOrderSendResponse = z.object({
  results: z.array(PurchaseOrderSendResult),
});
export type PurchaseOrderSendResponse = z.infer<typeof PurchaseOrderSendResponse>;

// ── Tancament amb pendent (OBR-82) ──────────────────────────────────────────────

/**
 * Què es demana a `close_purchase_order`: tancar una comanda deixant el pendent sense
 * rebre. El MOTIU és obligatori — la base de dades ho exigeix igualment — perquè
 * «entregada» vol dir que el gènere ha entrat, i quan no és així cal poder dir per què.
 */
export const PurchaseOrderCloseInput = z
  .object({
    purchase_order_id: uuid,
    reason: nonEmptyString.max(1000),
  })
  .strict();
export type PurchaseOrderCloseInput = z.infer<typeof PurchaseOrderCloseInput>;

/**
 * Una línia de la foto d'un tancament: el que es deixava sense rebre en aquell moment.
 * Quantitats en unitat base; `ordered_base_quantity` i `pending_base_quantity` són
 * NULL quan la línia va en un envàs sense conversió.
 */
export const PurchaseOrderClosureLine = z
  .object({
    purchase_order_line_id: uuid,
    article_id: uuid,
    ordered_base_quantity: nonNegativeQuantity.nullable(),
    received_base_quantity: nonNegativeQuantity,
    pending_base_quantity: nonNegativeQuantity.nullable(),
  })
  .strict();
export type PurchaseOrderClosureLine = z.infer<typeof PurchaseOrderClosureLine>;

/**
 * El registre d'un tancament — purchase_order_closures: qui, quan, el motiu i la foto
 * del que faltava. Append-only i escrit només per `close_purchase_order`: això és el
 * que se'n llegeix.
 */
export const PurchaseOrderClosure = z
  .object({
    id: uuid,
    purchase_order_id: uuid,
    reason: nonEmptyString,
    lines: z.array(PurchaseOrderClosureLine),
    closed_by: uuid,
    closed_at: z.string(),
  })
  .strict();
export type PurchaseOrderClosure = z.infer<typeof PurchaseOrderClosure>;

// ── Excepcions de recepció (OBR-83) ─────────────────────────────────────────────

/**
 * Una marca de l'operari sobre una entrada d'una recepció: «aquesta és diferent», amb la
 * nota que ho explica. Viatja dins la recepció que `sync-push` confirma. La nota és
 * OBLIGATÒRIA: una marca sense explicació no serveix al responsable que l'ha de revisar.
 */
export const ReceptionExceptionFlag = z
  .object({
    supplier_lot_id: uuid,
    note: nonEmptyString.max(1000),
  })
  .strict();
export type ReceptionExceptionFlag = z.infer<typeof ReceptionExceptionFlag>;

/** Una porció d'una devolució: quant d'un lot concret (unitat base de l'article). */
export const PurchaseReturnLotAllocation = z
  .object({
    supplier_lot_id: uuid,
    quantity: z.number().finite().positive(),
  })
  .strict();
export type PurchaseReturnLotAllocation = z.infer<typeof PurchaseReturnLotAllocation>;

export const RECEPTION_EXCEPTION_DECISIONS = ['accepted', 'rejected'] as const;
export type ReceptionExceptionDecision = (typeof RECEPTION_EXCEPTION_DECISIONS)[number];

/**
 * La llargada màxima de les observacions d'una devolució (OBR-117): la que fa servir el zod d'aquí, el
 * camp del diàleg i la que exigeix el servidor (`create_purchase_return` i `resolve_reception_exception`).
 */
export const PURCHASE_RETURN_OBSERVATIONS_MAX_LENGTH = 2000;

/**
 * Què es demana a `resolve_reception_exception`: acceptar o rebutjar una excepció. Acceptar
 * només en deixa constància (la nota és opcional); REBUTJAR exigeix nota, perquè «no» sense
 * dir per què no serveix a qui n'ha de fer el seguiment. La base de dades ho exigeix igual.
 * Cap de les dues reescriu la comanda.
 */
export const ReceptionExceptionResolveInput = z
  .object({
    exception_id: uuid,
    decision: z.enum(RECEPTION_EXCEPTION_DECISIONS),
    note: z.string().trim().max(1000).nullable().optional(),
    /**
     * Només en REBUTJAR (OBR-116): què se'n fa del gènere. Retorn immediat o pendent de
     * devolució; sense això el servidor no rebutja.
     */
    resolution: z.enum(RECEPTION_EXCEPTION_REJECTIONS).optional(),
    /**
     * Com es reparteix en lots d'AQUESTA recepció la quantitat afectada (l'excés, tot l'article
     * fora de comanda…). Una excepció d'un lot concret no en necessita: és el seu lot sencer. La
     * suma ha de coincidir amb la quantitat afectada; el servidor ho comprova.
     */
    allocations: z.array(PurchaseReturnLotAllocation).max(50).optional(),
    /**
     * Només en rebutjar amb retorn PENDENT (OBR-133): quan es preveu que el transportista passi a
     * recollir-ho. Amb retorn immediat no té sentit (ja ha sortit) i el servidor l'ignora.
     */
    planned_return_date: dateString.nullable().optional(),
    /**
     * Només en REBUTJAR (OBR-117): observacions opcionals que es desen a cada devolució que es crea
     * (a banda del motiu, que és la nota). El servidor rebutja el que passa del límit.
     */
    observations: z.string().trim().max(PURCHASE_RETURN_OBSERVATIONS_MAX_LENGTH).nullable().optional(),
  })
  .strict()
  .refine(
    (value) => value.decision === 'accepted' || (value.note ?? '').trim() !== '',
    { message: 'note_required_to_reject', path: ['note'] },
  )
  .refine(
    (value) => value.decision === 'accepted' || value.resolution !== undefined,
    { message: 'resolution_required_to_reject', path: ['resolution'] },
  );
export type ReceptionExceptionResolveInput = z.infer<typeof ReceptionExceptionResolveInput>;

/**
 * Una excepció de recepció — reception_exceptions: el que s'ha desviat d'una recepció, i
 * què en va decidir un responsable. Les obre `open_reception_exceptions` i les resol
 * `resolve_reception_exception`; això és el que se'n llegeix.
 */
export const ReceptionException = z
  .object({
    id: uuid,
    reception_id: uuid,
    kind: z.enum(RECEPTION_EXCEPTION_KINDS),
    article_id: uuid,
    purchase_order_line_id: uuid.nullable(),
    supplier_lot_id: uuid.nullable(),
    ordered_base_quantity: nonNegativeQuantity.nullable(),
    received_base_quantity: nonNegativeQuantity,
    excess_base_quantity: nonNegativeQuantity.nullable(),
    operator_note: z.string().nullable(),
    status: z.enum(RECEPTION_EXCEPTION_STATUSES),
    resolved_by: uuid.nullable(),
    resolved_at: z.string().nullable(),
    resolution_note: z.string().nullable(),
    /** Què es va fer del gènere en rebutjar-la (OBR-116); NULL si no és rebutjada o és d'abans. */
    rejection_resolution: z.enum(RECEPTION_EXCEPTION_REJECTIONS).nullable(),
    created_at: z.string(),
  })
  .strict();
export type ReceptionException = z.infer<typeof ReceptionException>;

// ── Devolucions de compra (OBR-117) i resolució d'incidències (OBR-116) ──────────────

/**
 * Crear una devolució (`create_purchase_return`): del lot d'una recepció, amb el motiu.
 * `returned` la crea ja retornada (el transportista se l'ha emportat); `incident_id` la
 * lliga a una incidència de compra. Sense incidència és una devolució d'«albarà».
 */
export const PurchaseReturnCreateInput = z
  .object({
    reception_id: uuid,
    supplier_lot_id: uuid,
    quantity: z.number().finite().positive(),
    reason: nonEmptyString.max(1000),
    observations: z.string().trim().max(PURCHASE_RETURN_OBSERVATIONS_MAX_LENGTH).nullable().optional(),
    incident_id: uuid.nullable().optional(),
    returned: z.boolean().optional(),
    /**
     * Quan es preveu la recollida o el retorn (OBR-133): és el que fa que l'App la ensenyi com a
     * d'avui, endarrerida o futura. Sense data, queda pendent «sense data prevista». No té sentit
     * si ja s'ha retornat (`returned`): el servidor l'ignora.
     */
    planned_return_date: dateString.nullable().optional(),
  })
  .strict();
export type PurchaseReturnCreateInput = z.infer<typeof PurchaseReturnCreateInput>;

/** Una devolució de compra tal com es llegeix (purchase_returns). */
export const PurchaseReturn = z
  .object({
    id: uuid,
    origin: z.enum(PURCHASE_RETURN_ORIGINS),
    reception_exception_id: uuid.nullable(),
    incident_id: uuid.nullable(),
    reception_id: uuid,
    purchase_order_id: uuid.nullable(),
    supplier_id: uuid,
    supplier_lot_id: uuid,
    article_id: uuid,
    quantity: z.number().finite().positive(),
    unit_id: uuid,
    reason: z.string(),
    observations: z.string().nullable(),
    status: z.enum(PURCHASE_RETURN_STATUSES),
    created_by: uuid,
    created_at: z.string(),
    returned_by: uuid.nullable(),
    returned_at: z.string().nullable(),
    movement_id: uuid.nullable(),
    /** Quan es preveu la recollida (OBR-133); NULL a les d'abans o si no se'n va dir cap. */
    planned_return_date: dateString.nullable(),
    /** El número de l'albarà de devolució (DV-…), assignat en confirmar la sortida (OBR-133). */
    return_number: z.string().nullable(),
    /**
     * Va néixer ja retornada: el transportista se l'ha emportat (retorn immediat, OBR-117). Opcional
     * perquè una lectura d'abans de la migració o el mirall de planta no el porten: sense valor és
     * «no immediata».
     */
    immediate: z.boolean().optional(),
  })
  .strict();
export type PurchaseReturn = z.infer<typeof PurchaseReturn>;

/**
 * Tancar una incidència (`resolve_incident`). Una incidència de COMPRA (amb albarà) exigeix
 * resolució; la d'OT no en porta. El servidor sap de quina mena és: aquí només la forma.
 */
export const IncidentResolveInput = z
  .object({
    incident_id: uuid,
    resolution: z.enum(INCIDENT_RESOLUTIONS).optional(),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .strict();
export type IncidentResolveInput = z.infer<typeof IncidentResolveInput>;

// ── Rectificació d'una recepció finalitzada (OBR-86) ───────────────────────────

/**
 * Una entrada (un lot de proveïdor) d'una rectificació. Només es canvia el que hi és:
 * un camp que falta no es toca. `quantity` és la quantitat NETA que ha de quedar (en
 * unitat base): 0 anul·la l'entrada. L'article i el proveïdor no es poden canviar.
 */
export const ReceptionCorrectionLineInput = z
  .object({
    supplier_lot_id: uuid,
    quantity: nonNegativeQuantity.optional(),
    supplier_lot_code: z.string().trim().max(100).nullable().optional(),
    expiry_date: dateString.nullable().optional(),
  })
  .strict()
  .refine(
    (v) => v.quantity !== undefined || v.supplier_lot_code !== undefined || v.expiry_date !== undefined,
    { message: 'nothing_to_change', path: ['quantity'] },
  );
export type ReceptionCorrectionLineInput = z.infer<typeof ReceptionCorrectionLineInput>;

/** El que es pot canviar de la capçalera de l'albarà: el número del proveïdor i la data. */
export const ReceptionCorrectionHeaderInput = z
  .object({
    supplier_delivery_note_number: nonEmptyString.max(100).optional(),
    reception_date: dateString.optional(),
  })
  .strict();
export type ReceptionCorrectionHeaderInput = z.infer<typeof ReceptionCorrectionHeaderInput>;

/**
 * Què es demana a `correct_reception`: rectificar un albarà ja finalitzat. El MOTIU és
 * obligatori (la base de dades l'exigeix igual) i cal que hi hagi alguna cosa a canviar.
 */
export const ReceptionCorrectionInput = z
  .object({
    reception_id: uuid,
    reason: nonEmptyString.max(1000),
    lines: z.array(ReceptionCorrectionLineInput).max(200),
    header: ReceptionCorrectionHeaderInput,
  })
  .strict()
  .refine((v) => v.lines.length > 0 || Object.keys(v.header).length > 0, {
    message: 'nothing_to_change',
    path: ['lines'],
  });
export type ReceptionCorrectionInput = z.infer<typeof ReceptionCorrectionInput>;

const HeaderChange = z.object({ from: z.string().nullable(), to: z.string().nullable() }).strict();

/** El que va passar a la comanda en rectificar: de quin estat a quin. */
export const ReceptionCorrectionOrderEffect = z
  .object({
    purchase_order_id: uuid,
    status_from: z.enum(PURCHASE_ORDER_STATUSES),
    status_to: z.enum(PURCHASE_ORDER_STATUSES),
  })
  .strict();
export type ReceptionCorrectionOrderEffect = z.infer<typeof ReceptionCorrectionOrderEffect>;

/** Una rectificació — reception_corrections: qui, quan, per què i què va passar a la comanda. */
export const ReceptionCorrection = z
  .object({
    id: uuid,
    seq: z.number().int(),
    reception_id: uuid,
    reason: z.string(),
    header_changes: z.record(z.string(), HeaderChange),
    order_effect: ReceptionCorrectionOrderEffect.nullable(),
    reception_cancelled: z.boolean(),
    corrected_by: uuid,
    corrected_at: z.string(),
  })
  .strict();
export type ReceptionCorrection = z.infer<typeof ReceptionCorrection>;

/**
 * Una entrada tocada per una rectificació — reception_correction_lines: el que valia abans i
 * el que val ara, el moviment que compensa la quantitat i l'existència del lot després.
 */
export const ReceptionCorrectionLine = z
  .object({
    id: uuid,
    correction_id: uuid,
    supplier_lot_id: uuid,
    article_id: uuid,
    quantity_before: nonNegativeQuantity,
    quantity_after: nonNegativeQuantity,
    supplier_lot_code_before: z.string().nullable(),
    supplier_lot_code_after: z.string().nullable(),
    expiry_date_before: dateString.nullable(),
    expiry_date_after: dateString.nullable(),
    movement_id: uuid.nullable(),
    lot_balance_after: z.number().finite(),
  })
  .strict();
export type ReceptionCorrectionLine = z.infer<typeof ReceptionCorrectionLine>;

/**
 * Un lot de proveïdor tal com és ARA — la vista supplier_lots_current: el rebut net de
 * rectificacions i el lot del proveïdor vigent, al costat del que es va rebre.
 */
export const SupplierLotCurrent = z
  .object({
    supplier_lot_id: uuid,
    tenant_id: uuid,
    reception_id: uuid,
    article_id: uuid,
    internal_lot_number: z.string(),
    unit_id: uuid,
    status: z.string(),
    expiry_date: dateString.nullable(),
    purchase_unit_id: uuid.nullable(),
    purchase_quantity: nonNegativeQuantity.nullable(),
    reception_control_result: z.string().nullable(),
    reception_control_note: z.string().nullable(),
    purchase_order_line_id: uuid.nullable(),
    supplier_lot_code: z.string().nullable(),
    received_supplier_lot_code: z.string().nullable(),
    original_base_quantity: z.number().finite(),
    current_base_quantity: z.number().finite(),
    corrected: z.boolean(),
  })
  .strict();
export type SupplierLotCurrent = z.infer<typeof SupplierLotCurrent>;

// ── PurchaseOrderLineFulfillment (view read shape) ────────────────────────────

/**
 * Read shape of the purchase_order_line_fulfillment view: ordered / received /
 * pending quantities per line, all expressed in the article base unit. Feeds the
 * derived comanda status (domain/purchasing.ts) and the future MRP proposal.
 */
export const PurchaseOrderLineFulfillment = z
  .object({
    tenant_id: uuid,
    purchase_order_id: uuid,
    purchase_order_line_id: uuid,
    article_id: uuid,
    ordered_quantity: nonNegativeQuantity,
    purchase_unit_id: uuid.nullable(),
    // NULL quan l'envàs de la línia no té conversió configurada per a l'article:
    // el demanat no es pot expressar en unitat base, i el pendent tampoc.
    ordered_base_quantity: nonNegativeQuantity.nullable(),
    received_base_quantity: nonNegativeQuantity,
    pending_base_quantity: nonNegativeQuantity.nullable(),
  })
  .strict();
export type PurchaseOrderLineFulfillment = z.infer<typeof PurchaseOrderLineFulfillment>;

// ── PurchaseOrderTemplate ────────────────────────────────────────────────────

/**
 * Plantilla de comanda de compra: la comanda recurrent d'un proveïdor, desada
 * perquè no s'hagi de tornar a muntar cada setmana. Un proveïdor en pot tenir més
 * d'una («Setmanal», «Reposició urgent»), i el que les distingeix és el nom.
 *
 * NO PORTA PREU. El preu pactat viu a la relació Article ↔ Proveïdor i es proposa
 * en crear la línia; desar-lo aquí seria tenir-ne dues còpies, i el dia que el
 * proveïdor apugés preus la plantilla seguiria dient el d'abans.
 */
export const PurchaseOrderTemplateInput = z
  .object({
    supplier_id: uuid,
    name: nonEmptyString,
    notes: nonEmptyString.nullable().optional(),
  })
  .strict();
export type PurchaseOrderTemplateInput = z.infer<typeof PurchaseOrderTemplateInput>;

/**
 * Una línia de plantilla: què es compra i quant se'n compra habitualment.
 * `purchase_unit_id` segueix el mateix conveni que la línia de comanda —null = la
 * unitat base de l'article— i la quantitat és la que la comanda proposarà,
 * sempre editable abans de confirmar-la.
 */
export const PurchaseOrderTemplateLineInput = z
  .object({
    article_id: uuid,
    quantity: positiveQuantity,
    purchase_unit_id: uuid.nullable(),
    sort_order: z.number().int().min(0),
  })
  .strict();
export type PurchaseOrderTemplateLineInput = z.infer<typeof PurchaseOrderTemplateLineInput>;
